import { useMemo, useCallback, useState } from 'react';
import type { EventDto } from '@/types/dtos/events/response/event.dto';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import { PRODUCT_KEYS } from '@/types/dtos/orders/response/product.dto';
import { useProductsStore } from '@/state/stores/use-products-store';
import { useQuotaBalances } from '@/state/queries/entitlements/use-quota-balances';
import { getOriginEventId } from '@/helpers/event.helper';
import {
  findQuotaBalanceForScopedProductContext,
  resolveQuotaScopeForEvent,
  resolveScopedProductBalanceContext,
} from '@/helpers/product-quota.helper';
import { useGetEventOrdersQuery } from '@/state/queries/orders/use-get-event-orders';
import { useCreateEventOrderMutation } from '@/state/mutations/orders/use-create-order';
import { useUpdateOrderMutation } from '@/state/mutations/orders/use-update-order';
import { ORDER_STATUS } from '@/types/dtos/orders/response/order.dto';
import { useNotifications } from '@/hooks/use-notifications';
import { useTranslations } from '@/i18n/use-translations';
import { useEventStore } from '@/state/stores/use-event-store';
import { getOrderLineItemProductId, mapOrderLineItemsToUpdatePayload } from '@/helpers/order-line-item.helper';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { EVENT_CATALOG_ORDERS_FEATURE } from '@/constants/feature-flags';

export type InsufficientContributorCreditsInfo = {
  remainingCredits: number;
  notInvitedCount: number;
  neededCredits: number;
};

/**
 * Same logic as {@link ContributorsList}: contributor feature credits vs contributors not yet invited at event level.
 */
export function useInsufficientContributorCreditsInfo(
  event: EventDto | undefined,
  contributors: ContributorDto[] | undefined,
): InsufficientContributorCreditsInfo | null {
  const catalogProducts = useProductsStore((s) => s.products);
  const { scopeType: quotaScopeType, scopeId: quotaScopeId } = resolveQuotaScopeForEvent(event);
  const { data: balances } = useQuotaBalances(quotaScopeType, quotaScopeId);
  const { hasAccess: hasCatalogOrderAccess } = useFeatureAccess(EVENT_CATALOG_ORDERS_FEATURE);

  return useMemo(() => {
    if (!hasCatalogOrderAccess) return null;
    // Query disabled / still loading → do not treat missing balances as 0 credits.
    if (balances === undefined) return null;

    const balanceCtx = resolveScopedProductBalanceContext(catalogProducts, PRODUCT_KEYS.EVENT_CONTRIBUTOR);
    if (!balanceCtx) return null;

    const contributorBalance =
      findQuotaBalanceForScopedProductContext(balances, balanceCtx) ??
      balances.find((b) => b.quotaKey === 'contributors');
    const notInvitedContributorsCount = contributors?.filter((contributor) => !contributor.invitedAt).length || 0;

    const remainingCredits = contributorBalance?.available ?? 0;

    if (remainingCredits < notInvitedContributorsCount) {
      return {
        remainingCredits,
        notInvitedCount: notInvitedContributorsCount,
        neededCredits: notInvitedContributorsCount - remainingCredits,
      };
    }

    return null;
  }, [hasCatalogOrderAccess, catalogProducts, balances, contributors]);
}

export function useAddContributorCreditsFromDeficit(insufficientInfo: InsufficientContributorCreditsInfo | null) {
  const event = useEventStore((s) => s.event);
  const catalogProducts = useProductsStore((s) => s.products);
  const originEventId = getOriginEventId(event);
  const { data: orders } = useGetEventOrdersQuery(originEventId);
  const { mutateAsync: createEventOrder } = useCreateEventOrderMutation();
  const { mutateAsync: updateOrder } = useUpdateOrderMutation();
  const notification = useNotifications();
  const t = useTranslations();
  const { hasAccess: hasCatalogOrderAccess } = useFeatureAccess(EVENT_CATALOG_ORDERS_FEATURE);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const handleAddMissingCredits = useCallback(async () => {
    const originEventId = getOriginEventId(event);
    if (!hasCatalogOrderAccess || !insufficientInfo || !catalogProducts || !originEventId) return;

    const balanceCtx = resolveScopedProductBalanceContext(catalogProducts, PRODUCT_KEYS.EVENT_CONTRIBUTOR);
    if (!balanceCtx) return;
    const { product: contributorProduct } = balanceCtx;

    try {
      const latestOrder = orders?.find(
        (order) => order.status === ORDER_STATUS.DRAFT || order.status === ORDER_STATUS.PAYMENT_PENDING,
      );

      if (latestOrder?.status === ORDER_STATUS.DRAFT) {
        const existingContributorLineItem = latestOrder.lineItems.find(
          (lineItem) => getOrderLineItemProductId(lineItem.product) === contributorProduct.id,
        );

        if (existingContributorLineItem) {
          await updateOrder({
            orderId: latestOrder.id,
            lineItems: mapOrderLineItemsToUpdatePayload(latestOrder.lineItems, {
              bumpProductId: contributorProduct.id,
              bumpQuantity: insufficientInfo.neededCredits,
            }),
          });
        } else {
          await updateOrder({
            orderId: latestOrder.id,
            lineItems: [
              ...mapOrderLineItemsToUpdatePayload(latestOrder.lineItems),
              {
                productId: contributorProduct.id,
                quantity: insufficientInfo.neededCredits,
              },
            ],
          });
        }
      } else {
        await createEventOrder({
          eventId: originEventId,
          lineItems: [
            {
              productId: contributorProduct.id,
              quantity: insufficientInfo.neededCredits,
            },
          ],
        });
      }

      setCheckoutOpen(true);
    } catch (error) {
      console.error('Failed to add missing credits:', error);
      notification.notifyError(t('event.detail.contributors.notifications.addCreditsError'));
    }
  }, [
    hasCatalogOrderAccess,
    insufficientInfo,
    catalogProducts,
    event,
    orders,
    createEventOrder,
    updateOrder,
    notification,
    t,
  ]);

  return { handleAddMissingCredits, checkoutOpen, setCheckoutOpen };
}
